import React from 'react';

// utils
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_LONG } from '../../util/types';
import { convertCategoriesToSelectTreeOptions, constructQueryParamName } from '../../util/search';

// component imports
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import IntegerRangeFilter from './IntegerRangeFilter/IntegerRangeFilter';
import AllowedRangeFilter from './AllowedRangeFilter/AllowedRangeFilter';
import SeatsFilter from './SeatsFilter/SeatsFilter';
import OriginCountryFilter from './OriginCountryFilter/OriginCountryFilter';

/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
const FilterComponent = props => {
  const {
    idPrefix,
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    listingCategories,
    listingFieldsConfig = [],
    marketplaceCurrency,
    selectedCurrency,
    exchangeRates,
    maxPriceEUR,
    intl,
    ...rest
  } = props;
  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType } = config;
  const { liveEdit, showAsPopup } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const prefix = idPrefix || 'SearchPage';
  const componentId = `${prefix}.${key.toLowerCase()}`;
  const name = key.replace(/\s+/g, '-');

  const findFieldConfig = keyName =>
    listingFieldsConfig.find(fieldConfig => fieldConfig.key === keyName);
  const getNumberFromConfig = (fieldConfig, keyPaths = []) => {
    if (!fieldConfig) {
      return null;
    }

    for (const keyPath of keyPaths) {
      const keys = keyPath.split('.');
      const value = keys.reduce((acc, keySegment) => {
        return acc && typeof acc === 'object' ? acc[keySegment] : null;
      }, fieldConfig);
      if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
      }
    }
    return null;
  };

  if (key === 'allowedRangeMin') {
    return null;
  }

  if (key === 'allowedRangeMax') {
    const minFieldConfig = findFieldConfig('allowedRangeMin');
    const maxFieldConfig = findFieldConfig('allowedRangeMax');

    if (minFieldConfig && maxFieldConfig) {
      let absoluteMin =
        getNumberFromConfig(minFieldConfig, ['minimum', 'numberConfig.minimum', 'filterConfig.min']) ??
        getNumberFromConfig(maxFieldConfig, ['minimum', 'numberConfig.minimum', 'filterConfig.min']);
      let absoluteMax =
        getNumberFromConfig(maxFieldConfig, ['maximum', 'numberConfig.maximum', 'filterConfig.max']) ??
        getNumberFromConfig(minFieldConfig, ['maximum', 'numberConfig.maximum', 'filterConfig.max']);
      let step =
        getNumberFromConfig(minFieldConfig, ['step', 'numberConfig.step', 'filterConfig.step']) ??
        getNumberFromConfig(maxFieldConfig, ['step', 'numberConfig.step', 'filterConfig.step']) ??
        1;

      absoluteMin = typeof absoluteMin === 'number' ? absoluteMin : 0;
      absoluteMax =
        typeof absoluteMax === 'number' && absoluteMax > absoluteMin
          ? absoluteMax
          : absoluteMin + Math.max(step, 1);
      step = typeof step === 'number' && step > 0 ? step : 1;

      const minParamName = constructQueryParamName(
        minFieldConfig.key,
        minFieldConfig.scope || 'public'
      );
      const maxParamName = constructQueryParamName(
        maxFieldConfig.key,
        maxFieldConfig.scope || 'public'
      );

      const fallbackLabel = intl.formatMessage({ id: 'AllowedRangeFilter.labelDefault' });
      const configuredLabel =
        maxFieldConfig.filterConfig?.label || minFieldConfig.filterConfig?.label || '';
      const labelMatchesKey = [maxFieldConfig.key, minFieldConfig.key].includes(configuredLabel);
      const filterLabel = labelMatchesKey ? fallbackLabel : configuredLabel || fallbackLabel;

      return (
        <AllowedRangeFilter
          id={componentId}
          label={filterLabel}
          name={name}
          queryParamNames={[minParamName, maxParamName]}
          initialValues={initialValues([minParamName, maxParamName], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={absoluteMin}
          max={absoluteMax}
          step={step}
          {...rest}
        />
      );
    }
  }

  if (config.custom?.component === 'AllowedRangeFilter') {
    const { queryParamNames = [], label: customLabel, min, max, step } = config.custom;
    const filterLabel =
      customLabel || intl.formatMessage({ id: 'AllowedRangeFilter.labelDefault' });
    return (
      <AllowedRangeFilter
        id={componentId}
        label={filterLabel}
        name={name}
        queryParamNames={queryParamNames}
        initialValues={initialValues(queryParamNames, liveEdit)}
        onSubmit={getHandleChangedValueFn(useHistoryPush)}
        min={min}
        max={max}
        step={step}
        {...rest}
      />
    );
  }

  // Default filters: price, keywords, dates
  switch (schemaType) {
    case 'category': {
      const { scope, isNestedEnum, nestedParams } = config;
      const queryParamNames = nestedParams?.map(p => constructQueryParamName(p, scope));
      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.categoryLabel' })}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={convertCategoriesToSelectTreeOptions(listingCategories)}
          isNestedEnum={isNestedEnum}
          {...rest}
        />
      );
    }
    case 'listingType': {
      const { scope, options } = config;
      const paramNames = [constructQueryParamName(key, scope)];
      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.listingTypeLabel' })}
          queryParamNames={[paramNames]}
          initialValues={initialValues(paramNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={options}
          {...rest}
        />
      );
    }
    case 'price': {
      const { min, max, step } = config;
      return (
        <PriceFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={max}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          selectedCurrency={selectedCurrency}
          exchangeRates={exchangeRates}
          maxPriceEUR={maxPriceEUR}
          {...rest}
        />
      );
    }
    case 'originCountry': {
      const queryParamNames = [constructQueryParamName(key, config.scope)];
      return (
        <OriginCountryFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.originCountryLabel' })}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
    }
    case 'keywords':
      return (
        <KeywordFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.keywordsLabel' })}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
    case 'dates': {
      const { dateRangeMode } = config;
      const isNightlyMode = dateRangeMode === 'night';
      return (
        <BookingDateRangeFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.datesLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          minimumNights={isNightlyMode ? 1 : 0}
          {...rest}
        />
      );
    }
    case 'seats': {
      return (
        <SeatsFilter
          id={componentId}
          name={name}
          label={intl.formatMessage({ id: 'FilterComponent.seatsLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
    }
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, filterType } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return filterType === 'SelectSingleFilter' ? (
        <SelectSingleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={enumOptions}
          isNestedEnum={false}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={enumOptions}
          schemaType={schemaType}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, searchMode } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return (
        <SelectMultipleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={enumOptions}
          schemaType={schemaType}
          searchMode={searchMode}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_LONG: {
      const { minimum, maximum, scope, step, filterConfig = {} } = config;
      const { label } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return (
        <IntegerRangeFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={minimum}
          max={maximum}
          step={step}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
